import type { AxiosResponse } from 'axios';
import difference from 'lodash/difference';
import intersection from 'lodash/intersection';
import type { SagaIterator } from 'redux-saga';
import { call, debounce, put, select, takeEvery, takeLeading } from 'redux-saga/effects';

import projectsApi, { ProjectTag } from '@app/api/projects';
import * as socketActions from '@app/sockets/socketActions';
import track, { AnalyticsEventName } from '@app/utils/analytics';
import bugsnag from '@app/utils/bugsnag';

import {
    getActiveCount,
    getProjectsListApi,
    getProjectsMainSearchApi,
    getProjectsTagsApi,
    postFilenamesRename,
    postSortProjectsApi,
    setProjectActiveBranchApi,
    setProjectsViewSizeApi,
    updateProjectTagsApi,
} from './api';
import { projectsActions } from './reducers';
import {
    getActivityProjectsMapping,
    getProjectsActiveTags,
    getProjectsAllTags,
    getProjectsIdsFromList,
    getProjectsPage,
} from './selectors';
import type {
    ActiveCountResponse,
    GetProjectsListResponse,
    GetProjectsMainSearchResponse,
    GetTagsApiResponse,
    PostFilenameRenameResponse,
    PostTagsApiResponse,
    SetProjectActiveBranchResponse,
} from './types';

function* getProjectsTags(): SagaIterator {
    try {
        const response = yield call(getProjectsTagsApi);
        const tagsResponse: GetTagsApiResponse = response.data;

        if (response.status === 200) {
            const previousAllTags = yield select(getProjectsAllTags);
            yield put(projectsActions.updateAllProjectsTagSuccess(tagsResponse.tags));

            const activeTags: string[] = yield select(getProjectsActiveTags);
            const allTags: string[] = yield select(getProjectsAllTags);
            const newActiveTags = intersection(activeTags, allTags);

            const activeTagsChanged = !![
                ...difference(activeTags, newActiveTags),
                ...difference(newActiveTags, activeTags),
            ].length;
            const allTagsDifference = [
                ...difference(allTags, previousAllTags),
                ...difference(previousAllTags, allTags),
            ];
            const projectDeletedActiveTag = !allTagsDifference.length && !!newActiveTags.length;

            if (activeTagsChanged || projectDeletedActiveTag) {
                yield put(projectsActions.setTagsToFilter(newActiveTags));
            }
        }
    } catch (e) {
        bugsnag.log(`Get Projects Tags failed, error: ${e}`);
    }
}

function* getProjectsList(tags: string[]): SagaIterator {
    yield put(projectsActions.setProjectListIsUpdating(true));

    try {
        const { data }: AxiosResponse<GetProjectsListResponse> = yield call(getProjectsListApi, tags);
        yield put(
            projectsActions.getProjectListSuccess({
                list: data.project_list,
                projectsTags: data.project_tags,
                badges: data.task_badges,
                hasMoreProjects: data.hasMore,
            }),
        );
    } catch (e) {
        bugsnag.log(`Get projects list error, error: ${e}`);
        yield put(projectsActions.getProjectListError());
    } finally {
        yield put(projectsActions.setProjectListIsUpdating(false));
    }
}

function* loadMoreProjects(): SagaIterator {
    yield put(projectsActions.setProjectListIsUpdating(true));

    try {
        const activeTags = yield select(getProjectsActiveTags);
        const currentPage: number = yield select(getProjectsPage);
        const nextPage = currentPage + 1;

        const { data }: AxiosResponse<GetProjectsListResponse> = yield call(getProjectsListApi, activeTags, nextPage);
        yield put(
            projectsActions.loadMoreProjectsSuccess({
                list: data.project_list,
                projectsTags: data.project_tags,
                badges: data.task_badges,
                hasMoreProjects: data.hasMore,
            }),
        );
    } catch (e) {
        bugsnag.log(`Load more projects error, error: ${e}`);
        yield put(projectsActions.getProjectListError());
    } finally {
        yield put(projectsActions.setProjectListIsUpdating(false));
    }
}

function* setProjectsViewSize(projectIds: string[], collapse: boolean) {
    try {
        yield setProjectsViewSizeApi(projectIds, collapse);
    } catch (e) {
        bugsnag.log(`Error while saving project views size, error: ${e}`);
    }
}

function* switchViewForAllProjects(collapse: boolean): SagaIterator {
    const state = yield select();
    const allProjectsIdsFromList = getProjectsIdsFromList(state);

    yield call(setProjectsViewSize, allProjectsIdsFromList, collapse);
}

function* switchViewForProject(action: ReturnType<typeof projectsActions.switchProjectView>) {
    yield call(setProjectsViewSize, [action.payload.projectId], action.payload.collapsed);
}

function* updateProjectTags(action: ReturnType<typeof projectsActions.updateProjectTags>): SagaIterator {
    yield put(projectsActions.setProjectsTagsAreUpdating(true));

    try {
        const response = yield call(updateProjectTagsApi, action.payload.projectId, action.payload.tags);
        const tagsResponse: PostTagsApiResponse = response.data;

        const hashes =
            tagsResponse.hashes?.map((hash) => ({
                hash: hash.hash,
                value: hash.tag,
            })) ?? [];

        if (response.status === 200) {
            yield put(
                projectsActions.updateProjectTagsSuccess({
                    projectId: action.payload.projectId,
                    hashes,
                }),
            );
            yield call(getProjectsTags);
        }
    } catch (e) {
        bugsnag.log(`Post Projects Tags failed, error: ${e}`);
        yield put(projectsActions.updateProjectTagsError(action.payload.projectId));
    }
}

function* setTagsToFilter(action: ReturnType<typeof projectsActions.setTagsToFilter>) {
    yield call(getProjectsList, action.payload);
}

function* setProjectActiveBranch(action: ReturnType<typeof projectsActions.setProjectActiveBranch>): SagaIterator {
    yield put(projectsActions.setProjectListIsUpdating(true));

    try {
        const response = yield call(setProjectActiveBranchApi, action.payload.projectId, action.payload.branch);
        const responseData: SetProjectActiveBranchResponse = response.data;

        if (responseData.result === 'success') {
            yield put(projectsActions.setProjectActiveBranchSuccess());

            const activeTags = yield select(getProjectsActiveTags);

            yield call(projectsApi.util.invalidateTags, [ProjectTag]);

            yield call(getProjectsList, activeTags);
        }
    } catch (e) {
        bugsnag.log(`Set project active branch error, error: ${e}`);
        yield put(projectsActions.setProjectListIsUpdating(false));
    }
}

function* setProjectsListOrder(action: ReturnType<typeof projectsActions.setProjectsListOrder>) {
    try {
        yield postSortProjectsApi(action.payload.map((project) => project.id));
    } catch (e) {
        bugsnag.log(`Set project active branch error, error: ${e}`);
    }
}

function* getProjectsMainSearch(action: ReturnType<typeof projectsActions.getProjectsMainSearch>): SagaIterator {
    try {
        const response = yield call(getProjectsMainSearchApi, action.payload);
        const searchResponse: GetProjectsMainSearchResponse = response.data;

        if (response.status === 200 && searchResponse.err === 0) {
            track(AnalyticsEventName.PROJECT_DASHBOARD_SEARCHED, {
                query: action.payload.query,
            });

            yield put(
                projectsActions.getProjectsMainSearchSuccess({
                    query: action.payload.query,
                    objectType: action.payload.objectType,
                    searchResults: searchResponse.data,
                }),
            );
        } else {
            bugsnag.log(`Get projects main search error, error: ${searchResponse.err}`);
            yield put(projectsActions.getProjectsMainSearchError());
        }
    } catch (e) {
        bugsnag.log(`Get projects main search error, error: ${e}`);
        yield put(projectsActions.getProjectsMainSearchError());
    }
}

function* renameProjectFilename(action: ReturnType<typeof projectsActions.renameProjectFilename>): SagaIterator {
    try {
        yield put(projectsActions.setProjectListIsUpdating(true));
        const response = yield call(postFilenamesRename, action.payload.filenameId, action.payload.newFilename);
        const renameResponse: PostFilenameRenameResponse = response.data;
        yield put(projectsActions.setProjectListIsUpdating(false));

        if (response.status === 200 && renameResponse.err === 0) {
            yield put(
                projectsActions.renameProjectFilenameSuccess({
                    projectId: action.payload.projectId,
                    filenameId: renameResponse.id,
                    newFilename: renameResponse.filename,
                }),
            );
        } else if (response.status === 200 && (renameResponse.err === 1 || renameResponse.err === 2)) {
            yield put(
                projectsActions.renameProjectFilenameError({
                    projectId: action.payload.projectId,
                    filenameId: renameResponse.id,
                    newFilename: renameResponse.filename,
                    error: renameResponse.err,
                    message: renameResponse.message,
                }),
            );
        } else {
            bugsnag.log(`Rename project filename error, error: ${renameResponse.err}`);
        }
    } catch (e) {
        yield put(projectsActions.setProjectListIsUpdating(false));
        bugsnag.log(`Rename project filename error, error: ${e}`);
    }
}

function* getActiveCountData(): SagaIterator {
    try {
        const state = yield select();
        const projects = getActivityProjectsMapping(state);

        if (!projects || projects.length === 0) {
            return;
        }

        const response = yield call(getActiveCount, projects);
        const activeResponse: ActiveCountResponse = response.data;
        yield put(projectsActions.setActiveCount(activeResponse));
    } catch (e) {
        bugsnag.log(`Get active count data error, error: ${e}`);
    }
}

function* initActiveCountSocketListener(): SagaIterator {
    yield debounce(2000, [socketActions.bgProcessCountChanged.match], getActiveCountData);
}

export default function* initProjectsSagas(): SagaIterator {
    yield takeEvery(projectsActions.updateProjectTags.match, updateProjectTags);
    yield takeEvery(projectsActions.setTagsToFilter.match, setTagsToFilter);
    yield takeEvery(projectsActions.setProjectActiveBranch.match, setProjectActiveBranch);
    yield takeEvery(projectsActions.projectsExpandAll.match, switchViewForAllProjects, false);
    yield takeEvery(projectsActions.projectsCollapseAll.match, switchViewForAllProjects, true);
    yield takeEvery(projectsActions.switchProjectView.match, switchViewForProject);
    yield takeEvery(projectsActions.setProjectsListOrder.match, setProjectsListOrder);
    yield takeEvery(projectsActions.getProjectsMainSearch.match, getProjectsMainSearch);
    yield takeEvery(projectsActions.renameProjectFilename.match, renameProjectFilename);
    yield takeEvery(projectsActions.getProjectListSuccess.match, getActiveCountData);
    yield takeEvery(projectsActions.loadMoreProjects.match, loadMoreProjects);
    yield takeLeading(projectsActions.initProjectsList.match, getActiveCountData);
    yield takeLeading(projectsActions.initProjectsList.match, initActiveCountSocketListener);
}
